import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { createCity, getCities, updateCity } from "../../api/adminService";

const EMPTY_FORM = {
  country: "IL",
  settlementCode: "",
  nameHebrew: "",
  nameEnglish: "",
  nameArabic: "",
  districtName: "",
  councilName: "",
};

export default function Cities() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [dialogCity, setDialogCity] = useState(null); // { city } for edit, {} for create, null for closed

  function loadCities() {
    setLoading(true);
    getCities({
      search: search || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load cities. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    loadCities();
  }

  async function handleSave(values) {
    const payload = {
      country: values.country.trim(),
      settlementCode: values.settlementCode.trim(),
      nameHebrew: values.nameHebrew.trim(),
      nameEnglish: values.nameEnglish.trim() || null,
      nameArabic: values.nameArabic.trim() || null,
      districtName: values.districtName.trim() || null,
      councilName: values.councilName.trim() || null,
    };

    if (dialogCity.city) {
      const updated = await updateCity(dialogCity.city.cityId, payload);
      setRows((prev) => prev.map((r) => (r.cityId === updated.cityId ? updated : r)));
      setSuccessMessage(`Updated ${updated.nameHebrew}.`);
    } else {
      await createCity(payload);
      setSuccessMessage(`Created ${payload.nameHebrew}.`);
      setPaginationModel((p) => ({ ...p, page: 0 }));
      loadCities();
    }
    setDialogCity(null);
  }

  const columns = useMemo(
    () => [
      { field: "nameHebrew", headerName: "Name (Hebrew)", flex: 1, minWidth: 180 },
      { field: "nameEnglish", headerName: "Name (English)", flex: 1, minWidth: 180, valueGetter: (v) => v || "-" },
      { field: "nameArabic", headerName: "Name (Arabic)", flex: 1, minWidth: 180, valueGetter: (v) => v || "-" },
      { field: "districtName", headerName: "District", flex: 1, minWidth: 160, valueGetter: (v) => v || "-" },
      { field: "country", headerName: "Country", width: 110 },
    ],
    []
  );

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 3, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <LocationCityOutlinedIcon sx={{ color: "primary.main" }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Cities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click a row to edit a city, including its Arabic name.
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          disableElevation
          startIcon={<AddIcon />}
          onClick={() => setDialogCity({ city: null })}
          sx={{ borderRadius: 2.5, px: 2.5, py: 1 }}
        >
          Add City
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" component="form" onSubmit={handleSearchSubmit} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { xs: "stretch", sm: "center" } }}>
          <TextField
            placeholder="Search by Hebrew, English or Arabic name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 300, flexGrow: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button type="submit" variant="outlined" sx={{ borderRadius: 2.5 }}>
            Search
          </Button>
          <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {rowCount.toLocaleString()} {rowCount === 1 ? "city" : "cities"}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.cityId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(params) => setDialogCity({ city: params.row })}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Paper>

      {dialogCity && (
        <CityDialog city={dialogCity.city} onClose={() => setDialogCity(null)} onSave={handleSave} />
      )}

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        message={successMessage}
      />
    </Box>
  );
}

function CityDialog({ city, onClose, onSave }) {
  const [values, setValues] = useState(
    city
      ? {
          country: city.country || "",
          settlementCode: city.settlementCode || "",
          nameHebrew: city.nameHebrew || "",
          nameEnglish: city.nameEnglish || "",
          nameArabic: city.nameArabic || "",
          districtName: city.districtName || "",
          councilName: city.councilName || "",
        }
      : EMPTY_FORM
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  function setField(field, value) {
    setValues((prev) => ({ ...prev, [field]: value }));
  }

  const isValid = values.country.trim() && values.settlementCode.trim() && values.nameHebrew.trim();

  async function submit() {
    setSaving(true);
    setError("");
    try {
      await onSave(values);
    } catch (err) {
      setError(err.response?.data || "Could not save this city.");
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {city ? "Edit City" : "Add City"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {city ? "Update this city's names and district." : "Create a new city entry."}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{typeof error === "string" ? error : "Something went wrong."}</Alert>}

          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField
                label="Country"
                value={values.country}
                onChange={(e) => setField("country", e.target.value.toUpperCase())}
                inputProps={{ maxLength: 2 }}
                helperText="2-letter code"
                fullWidth
                autoFocus
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 8 }}>
              <TextField
                label="Settlement Code"
                value={values.settlementCode}
                onChange={(e) => setField("settlementCode", e.target.value)}
                helperText="Must be unique"
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Name (Hebrew)"
                value={values.nameHebrew}
                onChange={(e) => setField("nameHebrew", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Name (English)"
                value={values.nameEnglish}
                onChange={(e) => setField("nameEnglish", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Name (Arabic)"
                value={values.nameArabic}
                onChange={(e) => setField("nameArabic", e.target.value)}
                fullWidth
                dir="rtl"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="District"
                value={values.districtName}
                onChange={(e) => setField("districtName", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Council"
                value={values.councilName}
                onChange={(e) => setField("councilName", e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!isValid || saving}
          onClick={submit}
          sx={{ borderRadius: 2.5, px: 3 }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
